"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { reduceMotion } from "@/lib/motion";
import { Bell, Check, Pen, Sparkle } from "@/components/ui/icons";
import { QSG_PULSE } from "@/content/quiksigns";
import { cn } from "@/lib/utils";

type Phase = "idle" | "running" | "done";
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * An agreement two-thirds signed, with a reminder queued for the last signer. "Send the
 * reminder" completes it and files the signed document against the records it belongs to.
 * Auto-plays once in view and can be replayed.
 */
export function SignMock() {
  const root = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const timers = useRef<number[]>([]);
  const played = useRef(false);

  const clear = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const run = useCallback(() => {
    clear();
    played.current = true;
    setPhase("running");
    timers.current.push(window.setTimeout(() => setPhase("done"), 1400));
  }, []);

  const reset = () => {
    clear();
    setPhase("idle");
  };

  useEffect(() => clear, []);

  useGSAP(
    () => {
      const el = root.current;
      if (!el || reduceMotion()) return;
      const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: "top 85%", toggleActions: PLAY_ONCE }, delay: 0.9 });
      tl.fromTo(
        gsap.utils.toArray("[data-mock-row]", el),
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.7, ease: "silk", stagger: 0.09 },
      );
      tl.call(() => {
        timers.current.push(window.setTimeout(() => !played.current && run(), 1600));
      });
    },
    { scope: root },
  );

  const running = phase === "running";
  const done = phase === "done";

  return (
    <div
      ref={root}
      className="w-full rounded-[22px] border border-white/[0.09] bg-[#0b0b0c] p-4 text-left text-white shadow-[0_40px_100px_-40px_rgba(0,0,0,0.9)] md:p-5"
    >
      <div data-mock-row="" className="flex items-center justify-between gap-3">
        <p className="truncate text-[11.5px] text-white/45 max-sm:whitespace-normal">{QSG_PULSE.path}</p>
        <span
          className={cn(
            "shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-medium transition-colors duration-500",
            done ? "bg-mint text-ink" : "bg-mint/10 text-mint",
          )}
        >
          {done ? "Completed" : "Awaiting 1"}
        </span>
      </div>

      {/* The agreement */}
      <div data-mock-row="" className="mt-4 flex items-center gap-2.5 rounded-[14px] bg-white/[0.04] p-3.5">
        <span className="grid size-8 shrink-0 place-items-center rounded-[10px] bg-white/[0.07]">
          <Pen className="size-4 text-mint" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-[13.5px] font-medium max-sm:whitespace-normal">{QSG_PULSE.doc.name}</p>
          <p className="mt-0.5 truncate text-[11px] text-white/45 max-sm:whitespace-normal">{QSG_PULSE.doc.meta}</p>
        </div>
      </div>

      {/* The signer chain */}
      <ul className="mt-2 space-y-1.5">
        {QSG_PULSE.signers.map((s, i) => {
          const signed = s.state === "Signed" || done;
          return (
            <li data-mock-row="" key={s.name} className="flex items-center gap-3 rounded-[14px] bg-white/[0.04] p-3">
              <span
                className={cn(
                  "grid size-8 shrink-0 place-items-center rounded-full text-[10.5px] font-medium tabular-nums transition-colors duration-500",
                  signed ? "bg-mint text-ink" : "bg-white/[0.06] text-white/50",
                )}
              >
                {signed ? <Check className="size-3.5" strokeWidth={2.6} /> : i + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px]">{s.name}</span>
                <span className="mt-0.5 block truncate text-[10.5px] text-white/40">{s.role}</span>
              </span>
              <span className={cn("shrink-0 text-[10.5px] font-medium", signed ? "text-mint" : "text-amber-400")}>
                {signed ? "Signed" : "Waiting"}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 border-t border-white/[0.07] pt-3">
        <AnimatePresence mode="wait" initial={false}>
          {done ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/45">{QSG_PULSE.result.label}</p>
                <p className="text-[13px] font-medium text-mint">{QSG_PULSE.result.value}</p>
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-white/45">{QSG_PULSE.linked.label}</span>
                {QSG_PULSE.linked.items.map((l) => (
                  <span key={l} className="rounded-full bg-mint/10 px-2.5 py-1 text-[10.5px] text-mint">
                    {l}
                  </span>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] text-white/35">{QSG_PULSE.footer}</p>
                <button type="button" onClick={reset} className="shrink-0 text-[11.5px] font-medium text-white/60 underline-offset-4 hover:text-white hover:underline">
                  Replay
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div key="cta" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3, ease: EASE }}>
              <p className="mb-2.5 flex items-center gap-2 text-[11.5px] text-white/50">
                <Bell className="size-3.5 shrink-0 text-amber-400" />
                {QSG_PULSE.reminder.label} · <span className="text-white/70">{QSG_PULSE.reminder.meta}</span>
              </p>
              <button
                type="button"
                onClick={run}
                disabled={running}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-white/[0.06] py-2.5 text-[12.5px] font-medium text-white/85 transition-colors hover:bg-white/[0.1] disabled:opacity-60"
              >
                <Sparkle className={cn("size-3.5 shrink-0 text-mint", running && "animate-pulse")} />
                {running ? "Collecting the last signature…" : "Send the reminder"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
